// @flow
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {
  Typography,
  Box,
  IconButton,
  Divider,
  ButtonGroup,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import {
  Add,
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  DeleteOutlined,
} from '@material-ui/icons';
import {useForm} from 'react-hook-form';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import {colors} from '../../../../assets/styles/colors';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';

import type {CategoryRequest} from '../../api';
import type {Category} from '../../../../entities';
import analytics from '../../../../analytics';

type Props = {|
  open: boolean,
  isSaving?: boolean,
  handleClose: () => void,
  categories?: Category[],
  createCategory: ({categoryRequest: CategoryRequest}) => void,
  editCategory: ({categoryRequest: CategoryRequest}) => void,
  deleteCategory: ({categoryRequest: CategoryRequest}) => void,
  sortCategories: ({sortMovement?: {[key: string]: any}}) => void,
  createCategorySuccess?: boolean,
  resetState: () => void,
|};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    alignItems: 'center',
    textAlign: 'center',
    // width: 320,
    // height: 240,
    padding: theme.spacing(4, 0),
  },
  content: {
    maxWidth: 500,
    width: '100%',
    margin: 'auto',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.375rem',
  },
  subtitle: {
    textAlign: 'start',
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  sectionTitle: {
    fontWeight: 600,
    textAlign: 'start',
  },
  categoryRow: {
    marginTop: theme.spacing(1),
    display: 'flex',
  },
  categoryTextField: {
    flexGrow: 1,
  },
  categoryButton: {
    minWidth: 96,
    width: 96,
    marginLeft: theme.spacing(1),
    borderRadius: '8px',
    backgroundColor: colors.grey200,
    boxShadow: 'none',
  },
  addButton: {
    padding: theme.spacing(1.75, 1),
    // color: 'white',
    backgroundColor: theme.palette.primary.main,
    // '&:hover': {
    //   // backgroundColor: theme.palette.primary.main,
    //   filter: 'brightness(95%)',
    // },
  },
  categoriesArray: {
    // maxHeight: '700px',
    // [theme.breakpoints.down('sm')]: {
    //   maxHeight: '350px',
    // },
    overflowY: 'auto',
    // padding: theme.spacing(1),
  },
  deleteButton: {
    // width: '100%',
    // padding: theme.spacing(1.75, 1),
    marginLeft: 1,
    borderRadius: '8px',
    color: theme.palette.error.main,
  },
  orderButton: {
    borderRadius: '8px',
    padding: 'unset',
    flex: 1,
    border: 'unset',
    backgroundColor: colors.grey200,
    '&:hover': {
      backgroundColor: colors.grey300,
      boxShadow: 'none',
    },
    '&:disabled': {
      border: 'unset',
    },
  },
  confirmButton: {
    marginTop: theme.spacing(3),
    width: 280,
    height: 40,
    // color:'white',
    fontWeight: 600,
    fontSize: '1.125rem',
  },
  inputAdornment: {
    padding: '14px !important',
  },
  customTextField: {
    padding: 0,
    flexWrap: 'nowrap',
    borderRadius: '8px',
    border: `1.25px solid ${colors.grey300}`,
    boxSizing: 'border-box',
    backgroundColor: 'white !important',
    color: colors.grey800,
    '& .MuiFilledInput-input': {
      padding: '15px',
    },
    '& .MuiInputBase-input.Mui-disabled': {
      backgroundColor: colors.grey200,
    },
    '&.Mui-focused': {
      border: `1px solid ${colors.grey500} `,
      backgroundColor: 'white ',
      color: `${colors.grey800} `,
    },
  },
  textInput: {
    borderRadius: '8px',
    // fontSize: '0.875rem',
    color: colors.grey800,
  },
  textInputLabel: {
    color: colors.grey600,
  },
}));

export default function EditCategoryModal(props: Props) {
  const {
    open,
    isSaving,
    handleClose,
    categories,
    createCategory,
    deleteCategory,
    sortCategories,
    editCategory,
    createCategorySuccess,
    resetState,
  } = props;

  const classes = useStyles();
  const {register, handleSubmit, errors, setValue, reset} = useForm();
  const [localCategories, setLocalCategories] = useState<Category[]>([]);
  const [ready, setReady] = useState(false);

  // const newCategoryRef = useRef(null);
  useEffect(() => {
    if (categories) {
      setLocalCategories(categories);
      setReady(false);
    }
  }, [categories]);

  const handleDeleteCategory = (id: string) => {
    deleteCategory({categoryRequest: {id}});
  };

  const handleAddCategorySubmit = data => {
    if (data.newCategory) {
      createCategory({categoryRequest: {name: data.newCategory}});
      reset(data);
    }
  };

  useEffect(() => {
    if (createCategorySuccess) {
      analytics.fireCustomEvent('Category Created', undefined, [
        'user-guiding',
      ]);
      setValue('newCategory', '');
    }
  }, [createCategorySuccess, setValue]);

  const [movement, setMovement] = useState({end: 0, init: 0});

  const handleChangeOrder = (index, number) => {
    setReady(true);
    const newCategories = [...localCategories];
    newCategories[index + number] = localCategories[index];
    newCategories[index] = localCategories[index + number];
    setLocalCategories(newCategories);
    setMovement({
      init: localCategories[index].id,
      end: localCategories[index + number].id,
    });
  };

  useEffect(() => {
    return () => resetState && resetState();
  }, [resetState]);

  useEffect(() => {
    if (localCategories && ready) {
      const sortCategoriesRequest = {
        end: {id: movement.end},
        init: {id: movement.init},
      };

      sortCategories({sortCategoriesRequest});
    }
  }, [localCategories, movement, ready, sortCategories]);

  const [editingCategory, setEditingCategory] = useState(-1);

  const handleEditCategory = (newName: string, category: Category) => {
    if (newName !== category.name) {
      editCategory({
        categoryRequest: {id: category.id, name: newName},
      });
    }
    setEditingCategory(-1);
  };

  const categoriesArray = localCategories
    ? localCategories.map((c: Category, index: number) => (
        <div key={`category-${index}`} className={classes.categoryRow}>
          {editingCategory === index ? (
            <TextField
              autoFocus
              className={classes.categoryTextField}
              variant="filled"
              fullWidth
              // type="text"
              // value={c.name}
              defaultValue={c.name}
              // disabled={true}
              onKeyPress={e => {
                e.stopPropagation();
                if (e.key === 'Enter') handleEditCategory(e.target.value, c);
                if (e.key === 'Escape') setEditingCategory(-1);
              }}
              onBlur={e => handleEditCategory(e.target.value, c)}
              InputProps={{
                endAdornment: (
                  <InputAdornment>
                    <IconButton
                      className={classes.deleteButton}
                      onClick={() => handleDeleteCategory(c.id)}
                    >
                      <DeleteOutlined />
                    </IconButton>
                  </InputAdornment>
                ),
                className: classes.customTextField,
                disableUnderline: true,
              }}
            />
          ) : (
            <CustomTextField
              className={classes.categoryTextField}
              type="text"
              value={c.name}
              // defaultValue={c.name}
              disabled={true}
              onClick={() => setEditingCategory(index)}
              InputProps={{
                endAdornment: (
                  <InputAdornment>
                    <IconButton
                      className={classes.deleteButton}
                      onClick={() => handleDeleteCategory(c.id)}
                    >
                      <DeleteOutlined />
                    </IconButton>
                  </InputAdornment>
                ),
                style: {
                  padding: 0,
                  flexWrap: 'nowrap',
                },
              }}
            />
          )}
          <ButtonGroup className={classes.categoryButton} variant="contained">
            <CustomButton
              // variant="contained"
              color="default"
              // size="large"
              onClick={() => handleChangeOrder(index, -1)}
              disabled={isSaving || index <= 0}
              className={`${classes.orderButton}`}
            >
              <ArrowUpwardRounded />
            </CustomButton>
            <CustomButton
              // variant="contained"
              color="default"
              // size="large"
              onClick={() => handleChangeOrder(index, 1)}
              disabled={isSaving || index >= localCategories.length - 1}
              className={`${classes.orderButton}`}
            >
              <ArrowDownwardRounded />
            </CustomButton>
          </ButtonGroup>
        </div>
      ))
    : [];

  return (
    <CustomModal open={open} handleCloseModal={handleClose}>
      <div className={classes.root}>
        <Box className={classes.content}>
          <Typography align="center" gutterBottom className={classes.title}>
            {categories && categories.length > 0
              ? 'Editar Categorías'
              : 'Crear Categoría'}
          </Typography>
          <Typography align="center" className={classes.subtitle}>
            Las categorías son importantes para ordenar los productos de tu
            tienda y así tus clientes puedan encontrar lo que buscan fácilmente.
          </Typography>
          <Divider className={classes.divider} />
          <Typography className={classes.sectionTitle} paragraph>
            Nombre de la categoría:
          </Typography>
          <div className={classes.categoryRow}>
            <CustomTextField
              className={classes.categoryTextField}
              label="Agrega nueva categoría"
              name="newCategory"
              error={!!errors.newCategory}
              helperText={errors.newCategory ? errors.newCategory.message : ''}
              inputRef={e => {
                register({
                  pattern: {
                    value: /^[A-Za-z]+$/,
                    message: 'Campo obligatorio',
                  },
                });
                e && e.focus();
              }}
              onKeyPress={e => {
                if (e.key === 'Enter') handleSubmit(handleAddCategorySubmit)();
              }}
              type="text"
              disabled={isSaving}
            />
            <CustomButton
              variant="contained"
              color="primary"
              // size="large"
              onClick={handleSubmit(handleAddCategorySubmit)}
              disabled={isSaving}
              className={`${classes.categoryButton} ${classes.addButton}`}
            >
              <Add />
            </CustomButton>
          </div>
          <Box className={classes.categoriesArray}>{categoriesArray}</Box>
          {/* <CustomButton
            variant="contained"
            color="primary"
            className={classes.confirmButton}
            onClick={handleClose}
          >
            Confirmar
          </CustomButton> */}
        </Box>
      </div>
    </CustomModal>
  );
}

EditCategoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  categories: PropTypes.array,
  createCategory: PropTypes.func.isRequired,
  editCategory: PropTypes.func.isRequired,
  deleteCategory: PropTypes.func.isRequired,
  sortCategories: PropTypes.func.isRequired,
  createCategorySuccess: PropTypes.bool,
  resetState: PropTypes.func,
};
